import React, { useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import {
  AppButton,
  AnimatedTextInput,
  PaymentMethodSelectorWithModal,
  AmountAndConversion,
  StickyHeaderNavigation,
} from '@/components'
import { TotalAndFees } from '@/features/send-money/components/TotalAndFees'
import { ConfirmAvatar } from './components/ConfirmAvatar'
import { useHandleSendRequest } from '@/features/send-request/hooks'
import { useSendStore } from '@/store/useSendStore'
import { useUserStore } from '@/store/useUserStore'
import { useGetWallet } from '@/domain/wallet/useCases/useGetWallet'
import { useGetWalletDataByCryptoCode } from '@/hooks/useGetWalletDataByCryptoCode'
import type { RecipientParams } from '@/types/sendRequest'
import type { CryptoCode, FiatCode } from '@/constants/currencyCodes'
import { currencyUtils } from '@/utils'
import useOffsets from '@/hooks/useOffsets'

const { formatCryptoAmount } = currencyUtils
// Get data directly as RecipientParams from location.state

export const SendRequestConfirmScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()

  const note = useSendStore((s) => s.note)
  const setNote = useSendStore((s) => s.setNote)
  const paymentMethod = useSendStore((s) => s.paymentMethod)
  const setPaymentMethod = useSendStore((s) => s.setPaymentMethod)
  const setRecipient = useSendStore((s) => s.setRecipient)
  const recipient = useSendStore((s) => s.recipient)

  // Get recipient data from location state (following RN version pattern)
  const recipientData = location.state as RecipientParams | undefined

  // Sync recipient with store (including phone number)
  // Important: only run once on mount
  useEffect(() => {
    if (recipientData?.id) {
      setRecipient({
        id: recipientData.id,
        name: recipientData.name,
        phone: recipientData.phoneNumber,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Initialize note from recipientData if available (from request)
  // Only run once when component mounts
  useEffect(() => {
    if (recipientData?.note) {
      setNote(recipientData.note)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Extract data from recipientData or fallback to store
  const {
    type: storeType,
    cryptoAmount: storeCryptoAmount,
    fiatAmount: storeFiatAmount,
  } = useSendStore()

  // Use data from navigation or fallback to store
  const type = recipientData?.type || storeType
  const cryptoAmount = recipientData
    ? {
        amount: recipientData.currencyValue || '0',
        code: recipientData.currencyCode || 'USDT',
      }
    : storeCryptoAmount
  const fiatAmount = recipientData
    ? {
        amount: recipientData.fiatValue || '0',
        code: recipientData.fiatCode || 'USD',
      }
    : storeFiatAmount

  // Get user wallet data
  const {
    data: walletData,
    isLoading: isWalletLoading,
    refetch: refetchWallet,
  } = useGetWallet()

  // Get specific currency data like React Native
  const walletCurrencyData = useGetWalletDataByCryptoCode({
    cryptoCode: cryptoAmount.code,
  })

  const setWallet = useUserStore((state) => state.setWallet)

  // Update wallet in store when data changes
  useEffect(() => {
    if (walletData) {
      setWallet(walletData)
    }
  }, [walletData, setWallet])

  // Check if user is trying to send to themselves - simplified for now
  const isMyProfile = false // TODO: implement proper check when user structure is clear

  // Force wallet refresh when component mounts
  useEffect(() => {
    if (!walletData && !isWalletLoading) {
      refetchWallet()
    }
  }, [walletData, isWalletLoading, refetchWallet])

  // Get recipient from store as fallback
  const storeRecipient = useSendStore((s) => s.recipient)

  // Guard: redirect if no recipient data AND no store recipient (like React Native RecipientGuard)
  const hasNoData = useMemo(
    () => !recipientData && !storeRecipient?.id,
    [recipientData, storeRecipient?.id],
  )

  useEffect(() => {
    if (hasNoData) {
      navigate('/send-request', { replace: true })
    }
  }, [hasNoData, navigate])

  // If no recipient data at all, show error UI (don't render main content)
  if (hasNoData) {
    return (
      <BaseView
        flex={1}
        bgColor="background.screen"
        justify="center"
        align="center"
        px={24}
      >
        <BaseText
          fontSize={18}
          fontFamily="semibold"
          color="label.default"
          textAlign="center"
          mb={16}
        >
          {t('translation:errorNoRecipientData', 'No recipient data found')}
        </BaseText>
        <BaseText
          fontSize={14}
          color="label.secondary"
          textAlign="center"
          mb={24}
        >
          {t('translation:redirecting', 'Redirecting...')}
        </BaseText>
      </BaseView>
    )
  }

  // Guard: check if sending to yourself (like React Native RecipientGuard)
  if (isMyProfile) {
    return (
      <BaseView
        flex={1}
        bgColor="background.screen"
        justify="center"
        align="center"
        px={24}
      >
        <BaseText
          fontSize={18}
          fontFamily="semibold"
          color="label.default"
          textAlign="center"
          mb={16}
        >
          {t(
            'translation:youCannotSentToYourself',
            'You cannot send to yourself',
          )}
        </BaseText>
        <AppButton
          label={t('form:actions.goBack', 'Go Back')}
          onPress={() => navigate(-1)}
        />
      </BaseView>
    )
  }

  // Create effective recipient data (from navigation or store fallback)
  const effectiveRecipient = useMemo(() => {
    return (
      recipientData || {
        id: storeRecipient?.id || '',
        name: storeRecipient?.name || t('translation:unnamed', 'Unnamed'),
        username: '',
        avatar: undefined,
        phoneNumber: storeRecipient?.phone,
        type: type,
        currencyValue: cryptoAmount.amount,
        currencyCode: cryptoAmount.code,
        fiatValue: fiatAmount.amount,
        fiatCode: fiatAmount.code,
        requestId: undefined,
        byApp: 'true',
      }
    )
  }, [
    recipientData,
    storeRecipient?.id,
    storeRecipient?.name,
    storeRecipient?.phone,
    t,
    type,
    cryptoAmount.amount,
    cryptoAmount.code,
    fiatAmount.amount,
    fiatAmount.code,
  ])

  // Use the new hook for send/request logic
  const { handleSendRequest, isLoading } = useHandleSendRequest({
    type,
    currencyValue: cryptoAmount.amount,
    currencyCode: cryptoAmount.code as CryptoCode,
    fiatAmount: fiatAmount.amount,
    fiatCode: fiatAmount.code as FiatCode,
    amount: formatCryptoAmount({
      value: Number(cryptoAmount.amount),
      cryptoCode: cryptoAmount.code,
      showDecimals: true,
      showCryptoCode: false,
    }),
    requestId: effectiveRecipient?.requestId,
    recipientPhone: recipientData?.phoneNumber, // Pass phone from navigation state
  })

  // Avatar props are now used directly in Avatar component

  // Use both methods for comparison
  const currencyData = walletData?.completeCurrencies?.find(
    (currency) =>
      currency.code === cryptoAmount.code ||
      currency.code.toLowerCase() === cryptoAmount.code.toLowerCase() ||
      currency.name?.toLowerCase() === cryptoAmount.code.toLowerCase(),
  )

  // Use React Native approach as primary
  const primaryCurrencyData = walletCurrencyData || currencyData

  const hasEnoughBalance = primaryCurrencyData
    ? Number(primaryCurrencyData.amount) >= Number(cryptoAmount.amount)
    : false

  return (
    <BaseView
      flex={1}
      bgColor="background.screen"
      style={{
        height: '100vh',
      }}
    >
      <StickyHeaderNavigation
        title={
          effectiveRecipient?.requestId
            ? t('form:actions.send')
            : type === 'send'
              ? t('form:actions.send')
              : t('form:actions.request')
        }
      />

      {/* Scrollable Content */}
      <BaseView
        flex={1}
        px={12}
        pt={0}
        pb={0}
        style={{
          paddingTop: `${offsets.top + 56}px`, // Header height + safe area
          overflowY: 'auto',
        }}
      >
        {/* User Info Header - as in RN UserInfosHeader */}
        <BaseView>
          <BaseView row mt={14} pb={18} gap={16} align="center">
            <ConfirmAvatar
              size={59}
              uri={effectiveRecipient?.avatar}
              iconSize={40}
              name={effectiveRecipient?.name || 'Unknown'}
              gradientColors={
                effectiveRecipient?.avatarBg
                  ? (String(effectiveRecipient.avatarBg).split(',') as [
                      string,
                      string,
                      ...string[],
                    ])
                  : undefined
              }
            />
            <BaseView>
              <BaseText
                fontSize={20}
                fontFamily="semibold"
                color="label.default"
              >
                {effectiveRecipient?.name ||
                  t('translation:unnamed', 'Unnamed')}
              </BaseText>
              <BaseText fontFamily="medium" color="label.clickable">
                {effectiveRecipient?.username ||
                  effectiveRecipient?.phoneNumber ||
                  ''}
              </BaseText>
            </BaseView>
          </BaseView>
        </BaseView>

        {/* Amount - as in RN AmountAndConversion */}
        {/* added amount formatting */}
        <BaseView gap={14}>
          <AmountAndConversion
            cryptoAmount={{
              amount: formatCryptoAmount({
                value: Number(cryptoAmount.amount),
                cryptoCode: cryptoAmount.code,
                showDecimals: true,
                showCryptoCode: false,
              }),
              code: cryptoAmount.code,
            }}
            fiatAmount={fiatAmount}
            showDecimals={true}
            shouldMaskStablecoinAsFiat={false}
          />
        </BaseView>

        {/* TotalAndFees - show only for send and not for wallet */}
        {type === 'send' && paymentMethod !== 'Wallet' && (
          <TotalAndFees
            recipientParams={{
              currencyValue: cryptoAmount.amount,
              currencyCode: cryptoAmount.code,
              type,
            }}
            paymentMethod={paymentMethod}
          />
        )}

        {/* Note Input - as in RN NoteInput */}
        <BaseView pt={24}>
          <AnimatedTextInput
            label={t('translation:leaveNote', 'Leave a note')}
            value={note}
            onChangeText={(newNote) => {
              setNote(newNote.replace(/\n/g, ''))
            }}
            maxLength={150}
            showCount
            isTextArea
            style={{
              minHeight: '100px',
            }}
          />
        </BaseView>

        {/* Bottom section - mt="auto" as in RN */}
        <BaseView mt="auto" gap={24}>
          {/* Payment Method Selection */}
          {type === 'send' && (
            <PaymentMethodSelectorWithModal
              selectedMethod={paymentMethod}
              onMethodChange={setPaymentMethod}
              hasEnoughBalance={hasEnoughBalance}
              disabled={isWalletLoading}
              shouldDisplayCurrencyIcon={true}
              currencyCode={cryptoAmount.code as CryptoCode}
            />
          )}

          {/* Send Button */}
          <div style={{ paddingBottom: offsets.bottom }}>
            <AppButton
              label={
                type === 'send'
                  ? t('form:actions.send')
                  : t('form:actions.request')
              }
              onPress={handleSendRequest}
              disabled={
                (type === 'send' &&
                  paymentMethod === 'Wallet' &&
                  (!hasEnoughBalance || isWalletLoading)) ||
                isLoading
              }
              isLoading={isLoading}
            />
          </div>
        </BaseView>
      </BaseView>
    </BaseView>
  )
}

export default SendRequestConfirmScreen
